import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { 
  UtensilsCrossed, 
  Clock, 
  MapPin, 
  Phone, 
  Star, 
  ChefHat,
  Wifi,
  Car,
  Music,
  ArrowRight,
  Sparkles,
  Award,
  Heart,
  Instagram,
  Facebook,
  Twitter,
  Mail,
  ChevronDown,
  Quote,
  Utensils,
  Coffee,
  Wine,
  Cake,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Gift,
  Calendar,
  CreditCard,
  Smartphone,
  CheckCircle,
  Play,
  X
} from 'lucide-react';
import { useApp } from '../context/AppContext';

const LandingPage: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [activeTab, setActiveTab] = useState('lunch');
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);


  const features = [
    { icon: ChefHat, title: 'Master Chefs', desc: 'Award-winning culinary experts', color: '#D4AF37' },
    { icon: Wifi, title: 'Free WiFi', desc: 'High-speed connectivity', color: '#CD7F32' },
    { icon: Car, title: 'Valet Parking', desc: 'Complimentary service', color: '#D4AF37' },
    { icon: Music, title: 'Live Music', desc: 'Weekend entertainment', color: '#800020' },
    { icon: Award, title: 'Premium Quality', desc: 'Finest ingredients', color: '#D4AF37' },
    { icon: Heart, title: 'Made with Love', desc: 'Passion in every dish', color: '#CD7F32' },
  ];

  const menuCategories = [
    { icon: Utensils, name: 'Main Course', count: '25+ dishes', gradient: 'from-[#D4AF37] to-[#CD7F32]' },
    { icon: Coffee, name: 'Beverages', count: '15+ drinks', gradient: 'from-[#CD7F32] to-[#800020]' },
    { icon: Wine, name: 'Bar & Spirits', count: '30+ options', gradient: 'from-[#800020] to-[#D4AF37]' },
    { icon: Cake, name: 'Desserts', count: '12+ sweets', gradient: 'from-[#D4AF37] to-[#CD7F32]' },
  ];

  const menuHighlights = [
    { name: 'Signature Wagyu Steak', price: '₹2,499', rating: 4.9, image: '🥩', tag: 'Chef Special', desc: 'Premium A5 Wagyu with truffle butter' },
    { name: 'Lobster Thermidor', price: '₹1,899', rating: 4.8, image: '🦞', tag: 'Popular', desc: 'Fresh Atlantic lobster in creamy sauce' },
    { name: 'Truffle Risotto', price: '₹1,299', rating: 4.9, image: '🍚', tag: 'Vegetarian', desc: 'Arborio rice with black truffle' },
    { name: 'Seared Salmon', price: '₹1,599', rating: 4.7, image: '🐟', tag: 'Healthy', desc: 'Norwegian salmon with herbs' },
    { name: 'Beef Wellington', price: '₹2,199', rating: 4.8, image: '🥧', tag: 'Signature', desc: 'Tender beef in puff pastry' },
    { name: 'Chocolate Soufflé', price: '₹799', rating: 4.9, image: '🍫', tag: 'Dessert', desc: 'Warm Belgian chocolate delight' },
  ];

  const testimonials = [
    { name: 'Priya Sharma', role: 'Food Blogger', text: 'An extraordinary dining experience! The ambiance and food quality are unmatched in the city. Every visit feels like a celebration.', rating: 5, avatar: '👩‍🦰' },
    { name: 'Rahul Mehta', role: 'Business Executive', text: 'Perfect for client meetings. Impeccable service and the chef\'s special never disappoints. Highly recommended for corporate dining.', rating: 5, avatar: '👨‍💼' },
    { name: 'Ananya Patel', role: 'Regular Customer', text: 'My family\'s favorite restaurant. We celebrate every special occasion here! The staff remembers our preferences.', rating: 5, avatar: '👩' },
    { name: 'Vikram Singh', role: 'Food Critic', text: 'A culinary masterpiece in every sense. The attention to detail and flavor profiles are exceptional. A must-visit destination.', rating: 5, avatar: '👨‍🍳' },
  ];

  const stats = [
    { value: '10+', label: 'Years of Excellence', icon: Award },
    { value: '50K+', label: 'Happy Customers', icon: Users },
    { value: '100+', label: 'Menu Items', icon: Utensils },
    { value: '4.9', label: 'Average Rating', icon: Star },
  ];

  const specialties = [
    { title: 'Private Dining', desc: 'Exclusive rooms for special occasions', icon: Users },
    { title: 'Chef\'s Table', desc: 'Interactive culinary experience', icon: ChefHat },
    { title: 'Wine Pairing', desc: 'Expert sommelier recommendations', icon: Wine },
    { title: 'Catering', desc: 'Premium catering services', icon: Gift },
  ];

  const timeSlots = {
    lunch: [
      { time: '12:00 PM', available: true },
      { time: '1:00 PM', available: true },
      { time: '2:00 PM', available: false },
    ],
    dinner: [
      { time: '7:00 PM', available: true },
      { time: '8:00 PM', available: false },
      { time: '9:00 PM', available: true },
    ]
  };

  const benefits = [
    { icon: Smartphone, title: 'Easy Ordering', desc: 'Order from your phone' },
    { icon: CreditCard, title: 'Secure Payment', desc: 'Multiple payment options' },
    { icon: Zap, title: 'Quick Service', desc: 'Fast order processing' },
    { icon: Shield, title: 'Quality Assured', desc: 'Premium ingredients' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B] overflow-x-hidden">
      {/* Video Modal */}
      <AnimatePresence>
        {showVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
            onClick={() => setShowVideo(false)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative w-full max-w-4xl aspect-video bg-[#1C1C1E] rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
              <div className="w-full h-full flex items-center justify-center text-[#D4AF37]">
                <p className="text-xl">Restaurant Tour Video</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Floating Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 px-4 py-3"
        style={{
          background: scrollY > 50 ? 'rgba(10, 10, 11, 0.95)' : 'transparent',
          backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
          borderBottom: scrollY > 50 ? '1px solid rgba(212, 175, 55, 0.2)' : 'none',
          transition: 'all 0.3s ease'
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' }}
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <UtensilsCrossed size={20} className="text-[#0A0A0B]" />
            </motion.div>
            <span className="text-[#D4AF37] font-bold text-lg">Hotel Hub</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            {['Menu', 'About', 'Reservations', 'Contact'].map((item) => (
              <button
                key={item}
                className="text-[#F5F5DC] hover:text-[#D4AF37] transition-colors text-sm font-medium"
                onClick={() => document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })}
              >
                {item}
              </button>
            ))}
          </div>

          <motion.button
            onClick={() => setCurrentScreen('welcome')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-5 py-2 rounded-full text-sm font-semibold"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
              color: '#0A0A0B'
            }}
          >
            Order Now
          </motion.button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-20 pb-12">
        {/* Enhanced Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-[500px] h-[500px] rounded-full mix-blend-screen filter blur-[100px] opacity-30"
            style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.2, 1], x: [0, 100, 0], y: [0, 50, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-[400px] h-[400px] rounded-full mix-blend-screen filter blur-[80px] opacity-20"
            style={{ background: 'radial-gradient(circle, #800020 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.3, 1], x: [0, -80, 0], y: [0, -40, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full mix-blend-screen filter blur-[120px] opacity-10"
            style={{ background: 'radial-gradient(circle, #CD7F32 0%, transparent 70%)' }}
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Floating particles */}
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 4 + 1,
                height: Math.random() * 4 + 1,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background: i % 2 === 0 ? '#D4AF37' : '#CD7F32',
                opacity: Math.random() * 0.5 + 0.2
              }}
              animate={{
                y: [0, -50, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center max-w-4xl mx-auto"
          style={{ opacity, scale }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="w-32 h-32 mx-auto mb-8 rounded-full flex items-center justify-center relative"
              style={{ 
                background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
                boxShadow: '0 0 60px rgba(212, 175, 55, 0.4), 0 0 120px rgba(212, 175, 55, 0.2)'
              }}
              animate={{ 
                boxShadow: [
                  '0 0 60px rgba(212, 175, 55, 0.4)',
                  '0 0 80px rgba(212, 175, 55, 0.6)',
                  '0 0 60px rgba(212, 175, 55, 0.4)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <UtensilsCrossed size={56} className="text-[#0A0A0B]" strokeWidth={2} />
              <motion.div
                className="absolute -top-2 -right-2"
                animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                transition={{ 
                  rotate: { duration: 4, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity }
                }}
              >
                <Sparkles size={28} className="text-[#D4AF37]" fill="#D4AF37" />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#D4AF37] text-sm tracking-[6px] uppercase mb-4 flex items-center justify-center gap-2"
          >
            <Sparkles size={12} />
            Established 2015
            <Sparkles size={12} />
          </motion.p>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-6xl md:text-8xl font-bold mb-6"
            style={{
              fontFamily: "'Playfair Display', serif",
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 30%, #D4AF37 60%, #CD7F32 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '4px'
            }}
          >
            Hotel Hub
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-[#F5F5DC] text-xl md:text-2xl mb-4 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Where Culinary Art Meets Elegance
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-[#AEAEB2] text-base md:text-lg max-w-2xl mx-auto mb-10"
          >
            Experience an unforgettable journey through flavors crafted by world-renowned chefs. 
            Every dish tells a story of passion, tradition, and innovation.
          </motion.p>


          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              onClick={() => setCurrentScreen('welcome')}
              whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(212, 175, 55, 0.5)" }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 rounded-full font-semibold text-lg flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
                color: '#0A0A0B',
                boxShadow: '0 4px 30px rgba(212, 175, 55, 0.3)'
              }}
            >
              <Utensils size={20} />
              Start Ordering
              <ArrowRight size={20} />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05, borderColor: '#D4AF37' }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 rounded-full font-semibold text-lg border-2 text-[#D4AF37] flex items-center gap-2"
              style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }}
              onClick={() => setShowVideo(true)}
            >
              <Play size={20} />
              Watch Tour
            </motion.button>
          </motion.div>

          {/* Quick Info Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex justify-center gap-4 mt-12 flex-wrap"
          >
            {[
              { icon: Clock, text: '11 AM - 11 PM' },
              { icon: MapPin, text: 'MG Road, Downtown' },
              { icon: Phone, text: '+91 98765 43210' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                whileHover={{ scale: 1.05, y: -2 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer"
                style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)' }}
              >
                <item.icon size={16} className="text-[#D4AF37]" />
                <span className="text-[#F5F5DC] text-sm">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => document.getElementById('stats')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="text-[#AEAEB2] text-xs tracking-widest">SCROLL</span>
            <ChevronDown size={20} className="text-[#D4AF37]" />
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 px-6 relative">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(212, 175, 55, 0.03) 50%, transparent 100%)' }} />
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(212, 175, 55, 0.2)' }}
              className="text-center p-6 rounded-2xl cursor-pointer"
              style={{
                background: 'rgba(28, 28, 30, 0.5)',
                border: '1px solid rgba(212, 175, 55, 0.15)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <stat.icon size={32} className="text-[#D4AF37] mx-auto mb-3" />
              <motion.h3
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#D4AF37', fontFamily: "'Playfair Display', serif" }}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.2, type: "spring" }}
              >
                {stat.value}
              </motion.h3>
              <p className="text-[#AEAEB2] text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>


      {/* Features Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4"
                style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              The Hotel Hub Experience
            </h2>
            <p className="text-[#AEAEB2] max-w-xl mx-auto">
              More than just a restaurant — it's a destination for those who appreciate the finer things in life
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(212, 175, 55, 0.2)' }}
                className="p-6 rounded-2xl text-center group cursor-pointer relative overflow-hidden"
                style={{
                  background: 'rgba(28, 28, 30, 0.6)',
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                     style={{ background: `radial-gradient(circle at center, ${feature.color}10 0%, transparent 70%)` }} />
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center relative z-10"
                  style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(205, 127, 50, 0.2) 100%)' }}
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <feature.icon size={28} className="text-[#D4AF37] group-hover:text-[#D4AF37] transition-colors" />
                </motion.div>
                <h3 className="text-[#F5F5DC] font-semibold text-lg mb-2 relative z-10">{feature.title}</h3>
                <p className="text-[#AEAEB2] text-sm relative z-10">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 px-6 relative">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(128, 0, 32, 0.05) 50%, transparent 100%)' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Special Services</p>
            <h2 className="text-4xl font-bold" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Exclusive Experiences
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {specialties.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="flex items-start gap-4 p-6 rounded-2xl"
                style={{
                  background: 'rgba(28, 28, 30, 0.6)',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}
              >
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(128, 0, 32, 0.2) 100%)' }}>
                  <item.icon size={24} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-[#F5F5DC] font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-[#AEAEB2] text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Categories */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Explore</p>
            <h2 className="text-4xl font-bold" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Our Menu Categories
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {menuCategories.map((cat, index) => (
              <motion.div
                key={cat.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="p-6 rounded-2xl text-center cursor-pointer relative overflow-hidden group"
                style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(128, 0, 32, 0.1) 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                <cat.icon size={36} className="text-[#D4AF37] mx-auto mb-3 relative z-10" />
                <h3 className="text-[#F5F5DC] font-semibold mb-1 relative z-10">{cat.name}</h3>
                <p className="text-[#D4AF37] text-sm relative z-10">{cat.count}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Menu Highlights */}
      <section id="menu" className="py-20 px-6 relative">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(128, 0, 32, 0.05) 50%, transparent 100%)' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Signature Dishes</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4"
                style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Chef's Recommendations
            </h2>
            <p className="text-[#AEAEB2]">Handpicked favorites that define our culinary excellence</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuHighlights.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03, y: -5, boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)' }}
                className="rounded-2xl overflow-hidden cursor-pointer group"
                style={{
                  background: 'rgba(28, 28, 30, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <div className="relative h-32 flex items-center justify-center text-6xl"
                     style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(128, 0, 32, 0.2) 100%)' }}>
                  {item.image}
                  <div className="absolute top-3 right-3">
                    <span className="text-xs px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A0A0B] font-semibold">
                      {item.tag}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-[#F5F5DC] font-semibold text-lg mb-2">{item.name}</h3>
                  <p className="text-[#AEAEB2] text-sm mb-3">{item.desc}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star size={14} className="text-[#D4AF37]" fill="#D4AF37" />
                      <span className="text-[#D4AF37] text-sm font-medium">{item.rating}</span>
                    </div>
                    <span className="text-[#D4AF37] font-bold text-xl">{item.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <motion.button
              onClick={() => setCurrentScreen('welcome')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 rounded-full font-semibold"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
                color: '#0A0A0B'
              }}
            >
              View Full Menu
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Reservation Section */}
      <section id="reservations" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Book a Table</p>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Reserve Your Experience
            </h2>
            <p className="text-[#AEAEB2]">Secure your spot at the finest dining destination</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-8 rounded-3xl"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {/* Tab Selector */}
            <div className="flex gap-2 mb-6">
              {['lunch', 'dinner'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex-1 py-3 rounded-xl font-semibold capitalize transition-all duration-300"
                  style={{
                    background: activeTab === tab ? 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' : 'rgba(212, 175, 55, 0.1)',
                    color: activeTab === tab ? '#0A0A0B' : '#D4AF37'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Time Slots */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {timeSlots[activeTab as keyof typeof timeSlots].map((slot, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: slot.available ? 1.05 : 1 }}
                  whileTap={{ scale: slot.available ? 0.95 : 1 }}
                  disabled={!slot.available}
                  className="py-3 rounded-xl font-medium transition-all duration-300"
                  style={{
                    background: slot.available ? 'rgba(212, 175, 55, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                    border: `1px solid ${slot.available ? 'rgba(212, 175, 55, 0.3)' : 'rgba(128, 128, 128, 0.2)'}`,
                    color: slot.available ? '#D4AF37' : '#666',
                    cursor: slot.available ? 'pointer' : 'not-allowed'
                  }}
                >
                  {slot.time}
                  {!slot.available && <span className="block text-xs mt-1">Booked</span>}
                </motion.button>
              ))}
            </div>

            <motion.button
              onClick={() => setCurrentScreen('welcome')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
                color: '#0A0A0B'
              }}
            >
              <Calendar size={20} />
              Reserve Now
            </motion.button>
          </motion.div>
        </div>
      </section>


      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Digital Experience</p>
            <h2 className="text-4xl font-bold" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Seamless Ordering
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(128, 0, 32, 0.2) 100%)' }}
                >
                  <benefit.icon size={32} className="text-[#D4AF37]" />
                </motion.div>
                <h3 className="text-[#F5F5DC] font-semibold mb-2">{benefit.title}</h3>
                <p className="text-[#AEAEB2] text-sm">{benefit.desc}</p>
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                  className="h-1 mx-auto mt-4 rounded-full"
                  style={{ background: 'linear-gradient(90deg, #D4AF37 0%, #CD7F32 100%)', maxWidth: '60px' }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Testimonials</p>
            <h2 className="text-4xl font-bold" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              What Our Guests Say
            </h2>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="p-8 md:p-10 rounded-3xl text-center"
                style={{
                  background: 'rgba(28, 28, 30, 0.6)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="text-6xl mb-6"
                >
                  {testimonials[currentTestimonial].avatar}
                </motion.div>
                <Quote size={40} className="text-[#D4AF37] mx-auto mb-6 opacity-30" />
                <p className="text-[#F5F5DC] text-xl md:text-2xl mb-6 leading-relaxed"
                   style={{ fontFamily: "'Playfair Display', serif" }}>
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div className="flex justify-center gap-1 mb-4">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.3 + i * 0.1, type: "spring" }}
                    >
                      <Star size={18} className="text-[#D4AF37]" fill="#D4AF37" />
                    </motion.div>
                  ))}
                </div>
                <h4 className="text-[#D4AF37] font-semibold text-lg">{testimonials[currentTestimonial].name}</h4>
                <p className="text-[#AEAEB2] text-sm">{testimonials[currentTestimonial].role}</p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  whileHover={{ scale: 1.2 }}
                  className="rounded-full transition-all duration-300"
                  style={{
                    background: index === currentTestimonial ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)',
                    width: index === currentTestimonial ? '32px' : '8px',
                    height: '8px'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-3">Gallery</p>
            <h2 className="text-4xl font-bold" style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              A Visual Journey
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['🍽️', '🍷', '👨‍🍳', '🎵', '🕯️', '🥂', '🍰', '🌹'].map((emoji, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="aspect-square rounded-2xl flex items-center justify-center text-6xl cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(128, 0, 32, 0.1) 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}
              >
                {emoji}
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-20 px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto p-10 md:p-12 rounded-3xl text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(128, 0, 32, 0.15) 100%)',
            border: '1px solid rgba(212, 175, 55, 0.3)'
          }}
        >
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute -top-20 -right-20 w-40 h-40 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)' }}
              animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(128, 0, 32, 0.3) 0%, transparent 70%)' }}
              animate={{ scale: [1, 1.3, 1], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity }}
            />
          </div>
          
          <div className="relative z-10">
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <Sparkles size={56} className="text-[#D4AF37]" />
            </motion.div>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-4"
                style={{ color: '#F5F5DC', fontFamily: "'Playfair Display', serif" }}>
              Ready for an Exceptional Experience?
            </h2>
            
            <p className="text-[#AEAEB2] mb-8 text-lg max-w-xl mx-auto">
              Join us for a memorable dining experience. Scan the QR at your table or order online for delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <motion.button
                onClick={() => setCurrentScreen('welcome')}
                whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(212, 175, 55, 0.5)" }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
                  color: '#0A0A0B',
                  boxShadow: '0 4px 30px rgba(212, 175, 55, 0.4)'
                }}
              >
                <Utensils size={20} />
                Get Started Now
                <ArrowRight size={20} />
              </motion.button>

              <motion.a
                href="tel:+919876543210"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 rounded-full font-semibold text-lg border-2 text-[#D4AF37] inline-flex items-center gap-2"
                style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }}
              >
                <Phone size={20} />
                Call to Reserve
              </motion.a>
            </div>

            <div className="flex items-center justify-center gap-6 text-[#AEAEB2] text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#D4AF37]" />
                <span>Instant Confirmation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#D4AF37]" />
                <span>No Booking Fee</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-6 border-t" style={{ borderColor: 'rgba(212, 175, 55, 0.1)' }}>
        <div className="max-w-5xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                     style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' }}>
                  <UtensilsCrossed size={24} className="text-[#0A0A0B]" />
                </div>
                <div>
                  <h3 className="text-[#D4AF37] font-bold text-xl">Hotel Hub</h3>
                  <p className="text-[#AEAEB2] text-sm">Fine Dining Experience</p>
                </div>
              </div>
              <p className="text-[#AEAEB2] text-sm mb-4 max-w-sm">
                Experience culinary excellence in the heart of the city. Where every meal is a celebration.
              </p>
              <div className="flex gap-3">
                {[
                  { icon: Instagram, link: '#' },
                  { icon: Facebook, link: '#' },
                  { icon: Twitter, link: '#' },
                  { icon: Mail, link: 'mailto:info@hotelhub.com' }
                ].map((social, i) => (
                  <motion.a
                    key={i}
                    href={social.link}
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)' }}
                  >
                    <social.icon size={18} className="text-[#D4AF37]" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-[#F5F5DC] font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {['Menu', 'Reservations', 'About Us', 'Contact'].map((link) => (
                  <li key={link}>
                    <button className="text-[#AEAEB2] hover:text-[#D4AF37] transition-colors text-sm">
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-[#F5F5DC] font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-[#AEAEB2] text-sm">
                  <MapPin size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>MG Road, Downtown, City 560001</span>
                </li>
                <li className="flex items-center gap-2 text-[#AEAEB2] text-sm">
                  <Phone size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span>+91 98765 43210</span>
                </li>
                <li className="flex items-center gap-2 text-[#AEAEB2] text-sm">
                  <Mail size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span>info@hotelhub.com</span>
                </li>
                <li className="flex items-center gap-2 text-[#AEAEB2] text-sm">
                  <Clock size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span>11 AM - 11 PM Daily</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 border-t flex flex-col md:flex-row items-center justify-between gap-4"
               style={{ borderColor: 'rgba(212, 175, 55, 0.1)' }}>
            <p className="text-[#AEAEB2] text-sm text-center md:text-left">
              © 2026 Hotel Hub. All rights reserved. Made with ❤️ for food lovers
            </p>
            <div className="flex gap-4 text-[#AEAEB2] text-sm">
              <button className="hover:text-[#D4AF37] transition-colors">Privacy Policy</button>
              <span>•</span>
              <button className="hover:text-[#D4AF37] transition-colors">Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setCurrentScreen('welcome')}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center z-40"
        style={{
          background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
          boxShadow: '0 8px 32px rgba(212, 175, 55, 0.4)'
        }}
      >
        <Utensils size={24} className="text-[#0A0A0B]" />
      </motion.button>
    </div>
  );
};

export default LandingPage;